import React, { useState } from 'react';

import {
  Schedule as CalendarIcon,
  CheckCircle as CheckIcon,
  Computer as ComputerIcon,
  Description as DocumentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MyLocation as GpsIcon,
  Group as GroupIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Email as MailIcon,
  Security as ShieldIcon,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';

import { Contract, getStatusColor } from '../contractManagement.utils';

interface ContractDetailsDialogProps {
  open: boolean;
  contract: Contract | null;
  onClose: () => void;
  onResendDocumentLink?: (
    contractId: string,
    signerUuid: string,
    signerEmail: string
  ) => Promise<void>;
  resendLoading?: boolean;
}

const ContractDetailsDialog: React.FC<ContractDetailsDialogProps> = ({
  open,
  contract,
  onClose,
  onResendDocumentLink,
  resendLoading = false,
}) => {
  const [expandedSigners, setExpandedSigners] = useState<Set<string>>(
    new Set()
  );
  const [resendingSigner, setResendingSigner] = useState<string | null>(null);

  if (!contract) return null;

  const { contract_id, contract_status, contract_details } = contract;
  const { levels, contractName } = contract_details;

  // Helper function to format timestamp
  const formatTimestamp = (timestampLocal: string | null) => {
    if (!timestampLocal) return null;

    try {
      const date = new Date(timestampLocal);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return timestampLocal; // Return as is if parsing fails
    }
  };

  // Helper function to check if level is actually completed
  const isLevelCompleted = (level: any) => {
    return level.signedSoFar >= level.requiredSignatures;
  };

  // Helper function to get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to check if signer has additional info
  const hasAdditionalInfo = (signer: any) => {
    return (
      signer.GPS ||
      signer.location ||
      signer.sourceIp ||
      signer.timezone ||
      signer.deviceUsed ||
      signer.postalCode
    );
  };

  // Helper function to toggle expanded state for a signer
  const toggleSignerExpanded = (signerUuid: string) => {
    const newExpanded = new Set(expandedSigners);
    if (newExpanded.has(signerUuid)) {
      newExpanded.delete(signerUuid);
    } else {
      newExpanded.add(signerUuid);
    }
    setExpandedSigners(newExpanded);
  };

  // Helper function to check if a signer is the current signer (next in line)
  const isCurrentSigner = (
    signer: any,
    levelIndex: number,
    signerIndex: number
  ) => {
    // If signer has already signed, they're not current
    if (signer.signed) return false;

    // Check if all previous levels are completed
    for (let i = 0; i < levelIndex; i++) {
      if (!isLevelCompleted(levels[i])) {
        return false; // Previous level not completed
      }
    }

    // Check if all previous signers in the same level have signed
    const currentLevel = levels[levelIndex];
    for (let i = 0; i < signerIndex; i++) {
      if (!currentLevel.signers[i].signed) {
        return false; // Previous signer in same level hasn't signed
      }
    }

    return true; // This is the current signer
  };

  // Helper function to handle resend document link
  const handleResendDocumentLink = async (signer: any) => {
    if (onResendDocumentLink) {
      setResendingSigner(signer.uuid);
      try {
        await onResendDocumentLink(contract_id, signer.uuid, signer.email);
      } finally {
        setResendingSigner(null);
      }
    } else {
      // Fallback for when no callback is provided
      // You can add your API call here
      // Example: resendDocumentLink(contract_id, signer.uuid, signer.email);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: '90vh',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 0,
          background: 'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
          color: 'white',
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                backdropFilter: 'blur(8px)',
              }}
            >
              <DocumentIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                Contract Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Review contract information and signatures
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, maxHeight: 'calc(90vh - 120px)', overflow: 'auto' }}
      >
        <Box sx={{ p: 3, spaceY: 3 }}>
          {/* Contract Information */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <ShieldIcon sx={{ color: '#1270dc', fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ color: '#1270dc', fontWeight: 600 }}
              >
                Contract Information
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Contract Name
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#333' }}
                  >
                    {contractName || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500, mb: 1 }}
                  >
                    Contract ID
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(0,0,0,0.05)',
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {contract_id}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, pt: 2, mt: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  Status:
                </Typography>
                <Chip
                  label={contract_status}
                  color={getStatusColor(contract_status)}
                  icon={
                    contract_status === 'Complete' ? <CheckIcon /> : undefined
                  }
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  Number of Levels:
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: '#1270dc' }}
                >
                  {levels.length}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Signing Levels */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(34, 197, 94, 0.05)',
              border: '2px solid rgba(34, 197, 94, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <GroupIcon sx={{ color: '#1270dc', fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ color: '#1270dc', fontWeight: 600 }}
              >
                Signing Levels
              </Typography>
            </Box>

            <Box sx={{ spaceY: 2 }}>
              {levels.map((level, levelIndex) => {
                const levelCompleted = isLevelCompleted(level);

                return (
                  <Box key={level.levelId} sx={{ mb: 3 }}>
                    {/* Level Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        bgcolor: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: 3,
                        border: '1px solid rgba(34, 197, 94, 0.2)',
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: '#333' }}
                        >
                          Level {level.levelId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Required Signatures: {level.requiredSignatures}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip
                          label={levelCompleted ? 'Complete' : 'In Progress'}
                          color={levelCompleted ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 600, mb: 1 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#333' }}
                        >
                          {level.signedSoFar}/{level.requiredSignatures} signed
                        </Typography>
                      </Box>
                    </Box>

                    {/* All Required Signatures Done */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        All Required Signatures Done:
                      </Typography>
                      <Chip
                        label={levelCompleted ? 'Yes' : 'No'}
                        color={levelCompleted ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {/* Signers */}
                    <Box ml={5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <GroupIcon sx={{ color: '#333', fontSize: 16 }} />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: '#333' }}
                        >
                          Signers ({level.signers.length})
                        </Typography>
                      </Box>

                      <Box sx={{ spaceY: 2.5 }}>
                        {level.signers.map((signer, signerIndex) => {
                          const formattedTimestamp = formatTimestamp(
                            signer.timestampLocal
                          );
                          const isExpanded = expandedSigners.has(signer.uuid);
                          const isCurrent = isCurrentSigner(
                            signer,
                            levelIndex,
                            signerIndex
                          );

                          return (
                            <Paper
                              key={signer.uuid}
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                bgcolor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                  boxShadow:
                                    '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                },
                                transition: 'box-shadow 0.2s ease-in-out',
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor:
                                          'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
                                        color: 'white',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {getInitials(signer.signerFullName)}
                                    </Avatar>
                                    <Box>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            fontWeight: 600,
                                            color: '#333',
                                          }}
                                        >
                                          {signer.signerFullName}
                                        </Typography>
                                        {isCurrent && !signer.signed && (
                                          <Chip
                                            label="Current"
                                            size="small"
                                            color="primary"
                                            sx={{
                                              fontSize: '0.625rem',
                                              height: 20,
                                              fontWeight: 600,
                                              bgcolor: '#1270dc',
                                              color: 'white',
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.875rem' }}
                                      >
                                        {signer.email}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {signer.signed && formattedTimestamp && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        ml: 4.5,
                                      }}
                                    >
                                      <CalendarIcon
                                        sx={{ fontSize: 12, color: '#22c55e' }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: '0.75rem',
                                          color: '#22c55e',
                                          fontWeight: 500,
                                        }}
                                      >
                                        Signed on: {formattedTimestamp}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  <Chip
                                    icon={
                                      signer.signed ? <CheckIcon /> : undefined
                                    }
                                    label={signer.signed ? 'Yes' : 'Pending'}
                                    color={
                                      signer.signed ? 'success' : 'default'
                                    }
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />

                                  {/* Resend Document Link Button */}
                                  <Button
                                    onClick={() =>
                                      handleResendDocumentLink(signer)
                                    }
                                    disabled={
                                      !isCurrent ||
                                      resendingSigner === signer.uuid
                                    }
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.75rem',
                                      py: 0.5,
                                      px: 1.5,
                                      minWidth: 'auto',
                                      textTransform: 'none',
                                      borderColor: isCurrent
                                        ? '#1270dc'
                                        : '#e0e0e0',
                                      color: isCurrent ? '#1270dc' : '#9e9e9e',
                                      '&:hover': {
                                        borderColor: isCurrent
                                          ? '#0d5aa7'
                                          : '#e0e0e0',
                                        bgcolor: isCurrent
                                          ? 'rgba(18, 112, 220, 0.04)'
                                          : 'transparent',
                                      },
                                      '&:disabled': {
                                        borderColor: '#e0e0e0',
                                        color: '#9e9e9e',
                                      },
                                    }}
                                  >
                                    {resendingSigner === signer.uuid
                                      ? 'Resending...'
                                      : 'Resend Link'}
                                  </Button>
                                </Box>
                              </Box>

                              {/* More Info Section */}
                              {hasAdditionalInfo(signer) && (
                                <Box sx={{ mt: 2 }}>
                                  <Button
                                    onClick={() =>
                                      toggleSignerExpanded(signer.uuid)
                                    }
                                    startIcon={
                                      isExpanded ? (
                                        <ExpandLessIcon />
                                      ) : (
                                        <ExpandMoreIcon />
                                      )
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      color: '#1270dc',
                                      fontWeight: 600,
                                      p: 0,
                                      minWidth: 'auto',
                                      '&:hover': {
                                        bgcolor: 'transparent',
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    More Info
                                  </Button>

                                  {isExpanded && (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'rgba(18, 112, 220, 0.05)',
                                        borderRadius: 2,
                                        border:
                                          '1px solid rgba(18, 112, 220, 0.2)',
                                      }}
                                    >
                                      <Grid container spacing={2}>
                                        {signer.GPS && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <GpsIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                GPS Location:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.GPS}
                                            </Typography>
                                          </Grid>
                                        )}

                                        {signer.location && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <LocationIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                Location:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.location}
                                            </Typography>
                                          </Grid>
                                        )}

                                        {signer.sourceIp && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <MailIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                Source IP:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.sourceIp}
                                            </Typography>
                                          </Grid>
                                        )}

                                        {signer.timezone && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <LanguageIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                Timezone:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.timezone}
                                            </Typography>
                                          </Grid>
                                        )}

                                        {signer.deviceUsed && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <ComputerIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                Device Used:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.deviceUsed}
                                            </Typography>
                                          </Grid>
                                        )}

                                        {signer.postalCode && (
                                          <Grid item xs={12} sm={6}>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <LocationIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: '#1270dc',
                                                }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#333',
                                                }}
                                              >
                                                Postal Code:
                                              </Typography>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {signer.postalCode}
                                            </Typography>
                                          </Grid>
                                        )}
                                      </Grid>
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: 'rgba(0,0,0,0.02)',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #1270dc 0%, #42d392 100%)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              opacity: 0.9,
            },
            transition: 'opacity 0.2s ease-in-out',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractDetailsDialog;
